import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { AppShell } from "../components/AppShell";
import { formatINR } from "../lib/brand";
import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Package,
  MessageSquare,
  DollarSign,
  Rocket,
  Zap,
  Tag,
  XCircle,
  Link2,
  Star,
  Store,
  Archive,
  ExternalLink,
  Lightbulb,
} from "lucide-react";

function Section({ title, icon: Icon, children, testId }) {
  return (
    <div className="bg-white/[0.025] backdrop-blur-xl border border-white/[0.08] rounded-xl p-6 text-white" data-testid={testId}>
      <div className="flex items-center gap-2 mb-4">
        <Icon size={16} className="text-[#4F46E5]" />
        <div className="text-sm font-bold uppercase tracking-[0.2em]">{title}</div>
      </div>
      {children}
    </div>
  );
}

/* Ads — Scale / Avoid grouped by category, Top-10 per category, with Style ID.
   Falls back to a single "All SKUs" group when the seller's upload lacks
   a category column so the panel still renders. */
function AdsByCategory({ items, tone = "scale", emptyText }) {
  if (!items || items.length === 0) {
    return <div className="text-xs text-slate-500">{emptyText}</div>;
  }
  const groups = {};
  for (const it of items) {
    const cat = (it.category && String(it.category).trim()) || "Uncategorised";
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(it);
  }
  // Order categories by total volume (sum of orders) descending
  const orderedCats = Object.keys(groups).sort(
    (a, b) =>
      (groups[b].reduce((s, x) => s + (x.orders || 0), 0)) -
      (groups[a].reduce((s, x) => s + (x.orders || 0), 0))
  );
  const rowBg = tone === "scale" ? "bg-green-50" : "bg-red-50";
  const numCls = tone === "scale" ? "text-green-700" : "text-red-700";
  return (
    <div className="space-y-4">
      {orderedCats.map((cat) => (
        <div key={cat} data-testid={`ads-${tone}-cat-${cat}`}>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600 mb-2 flex items-center gap-2">
            <Tag size={11} className="text-[#4F46E5]" />
            <span>{cat}</span>
            <span className="text-slate-400 font-mono-data">· top {Math.min(10, groups[cat].length)}</span>
          </div>
          <div className="space-y-2">
            {groups[cat].slice(0, 10).map((s, i) => (
              <div key={i} className={`flex items-center justify-between gap-3 p-2.5 rounded-md ${rowBg}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-bold truncate text-slate-900">{s.sku}</div>
                  {s.style_id && (
                    <div className="text-[10px] text-slate-500 font-mono-data truncate">Style ID · {s.style_id}</div>
                  )}
                </div>
                <span className={`text-xs font-mono-data shrink-0 ${numCls}`}>
                  {tone === "scale"
                    ? `${s.orders} orders · ${s.delivery_rate_pct}% delivery${s.return_rate_pct > 0 ? ` · ${s.return_rate_pct}% returns` : ""}`
                    : `${Math.max(s.cancel_rate_pct || 0, s.return_rate_pct || 0)}% ${(s.return_rate_pct || 0) > (s.cancel_rate_pct || 0) ? "returns" : "cancel"} · ${s.orders} orders`}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function StatTile({ label, value, hint, color = "zinc" }) {
  const colorMap = {
    zinc: "text-slate-200",
    red: "text-[#4F46E5]",
    green: "text-green-700",
    amber: "text-amber-600",
  };
  return (
    <div className="p-3 rounded-md bg-white/[0.02]">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
      <div className={`font-mono-data text-2xl font-black ${colorMap[color]}`}>{value}</div>
      {hint && <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}

function LiveAuditSection({ audit }) {
  if (!audit) return null;
  if (!audit.enabled) {
    const hint = audit.error_hint
      || (audit.reason === "ip_blocked"
        ? "This platform blocks requests from cloud servers. Use a residential proxy to unlock."
        : audit.reason === "no_products_found"
        ? "Page fetched but no product data found. Try a search or category URL."
        : audit.reason === "platform_blocked"
        ? "Platform is blocking automated requests. Report is generated from CSV only."
        : audit.reason === "location_required"
        ? "Quick-commerce platforms only load products after picking a serviceable address. Coming soon — report uses CSV only for now."
        : `Reason: ${audit.reason || "unknown"}.`);
    return (
      <div className="mb-6 bg-white/[0.025] backdrop-blur-xl border border-white/[0.08] rounded-xl p-5 text-white border-l-4 border-amber-400" data-testid="live-audit-disabled">
        <div className="flex items-start gap-3">
          <AlertTriangle size={18} className="text-amber-500 shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-bold">Live catalogue unavailable</div>
            <div className="text-xs text-slate-400 mt-1">{hint}</div>
          </div>
        </div>
      </div>
    );
  }

  const h = audit.catalogue_health || {};
  return (
    <div className="mb-8 bg-white/[0.025] backdrop-blur-xl border border-white/[0.08] rounded-xl p-6 text-white" data-testid="live-audit">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Store size={18} className="text-[#4F46E5]" />
          <div className="text-sm font-bold uppercase tracking-[0.2em]">Live Catalogue Audit</div>
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-data">
            · {audit.platform}
          </span>
        </div>
        {audit.source_url && (
          <a
            href={audit.source_url}
            target="_blank"
            rel="noreferrer"
            className="text-[11px] font-bold text-[#4F46E5] inline-flex items-center gap-1 hover:underline"
            data-testid="live-audit-source-link"
          >
            View source <ExternalLink size={10} />
          </a>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatTile label="Live products" value={h.live_product_count || 0} hint="currently listed" />
        <StatTile
          label="Match rate"
          value={`${h.match_rate_pct || 0}%`}
          hint={`${h.matched_count}/${h.csv_sku_count} CSV SKUs matched`}
        />
        <StatTile label="Dead SKUs" value={h.dead_sku_count || 0} hint="listed but never sold" color="amber" />
        <StatTile label="Out-of-stock" value={h.oos_count || 0} hint="currently unavailable" color="red" />
      </div>

      {/* Top visuals */}
      {(audit.top_visuals || []).length > 0 && (
        <div className="mb-6" data-testid="live-top-visuals">
          <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-3">
            Your top sellers — live
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {audit.top_visuals.slice(0, 5).map((v, i) => (
              <a
                key={i}
                href={v.url}
                target="_blank"
                rel="noreferrer"
                className="group block rounded-lg overflow-hidden bg-white/[0.02] hover:ring-2 hover:ring-[#4F46E5]/60 transition-all"
              >
                <div className="aspect-square overflow-hidden bg-white/[0.04]">
                  {v.image_url ? (
                    <img
                      src={v.image_url}
                      alt={v.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-300">
                      <Package size={28} />
                    </div>
                  )}
                </div>
                <div className="p-2">
                  <div className="text-[10px] text-slate-500 truncate">{v.title || v.sku}</div>
                  <div className="flex items-center justify-between mt-0.5">
                    <span className="font-mono-data text-xs font-bold">
                      ₹{Number(v.live_price || 0).toLocaleString("en-IN")}
                    </span>
                    {v.rating > 0 && (
                      <span className="text-[10px] text-amber-600 inline-flex items-center gap-0.5">
                        <Star size={9} fill="currentColor" /> {v.rating}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-500 font-mono-data">{v.orders} orders</div>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Price audit */}
        <div data-testid="live-price-audit">
          <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-3">
            Price Audit — CSV vs Live
          </div>
          {(audit.price_audit || []).length === 0 && (
            <div className="text-xs text-slate-500">No SKU matches between CSV and live catalogue.</div>
          )}
          <div className="space-y-2">
            {(audit.price_audit || []).slice(0, 6).map((p, i) => {
              const up = p.variance_pct > 0.5;
              const down = p.variance_pct < -0.5;
              return (
                <div key={i} className="flex items-center gap-3 p-2.5 rounded-md bg-white/[0.02]">
                  {p.image_url ? (
                    <img src={p.image_url} alt="" className="w-10 h-10 object-cover rounded" />
                  ) : (
                    <div className="w-10 h-10 bg-white/[0.05] rounded" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold truncate">{p.sku}</div>
                    <div className="text-[10px] text-slate-500 font-mono-data">
                      CSV ₹{p.csv_avg_price} → Live ₹{p.live_price}
                    </div>
                  </div>
                  <span
                    className={`text-[10px] font-bold px-2 py-1 rounded inline-flex items-center gap-0.5 ${
                      up ? "bg-green-100 text-green-700" : down ? "bg-red-100 text-[#4F46E5]" : "bg-white/[0.05] text-slate-300"
                    }`}
                  >
                    {up ? <TrendingUp size={11} /> : down ? <TrendingDown size={11} /> : null}
                    {p.variance_pct > 0 ? "+" : ""}{p.variance_pct}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* OOS */}
        <div data-testid="live-oos">
          <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-3">
            Out-of-Stock Right Now
          </div>
          {(audit.oos_alerts || []).length === 0 && (
            <div className="text-xs text-slate-500">Every live product is currently in stock. ✓</div>
          )}
          <div className="space-y-2">
            {(audit.oos_alerts || []).slice(0, 6).map((o, i) => (
              <a
                key={i}
                href={o.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 p-2.5 rounded-md bg-red-50 text-slate-800 hover:bg-red-100 transition-colors"
              >
                {o.image_url ? (
                  <img src={o.image_url} alt="" className="w-10 h-10 object-cover rounded" />
                ) : (
                  <div className="w-10 h-10 bg-white/[0.05] rounded" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold truncate">{o.title}</div>
                  {o.matched_csv_sku && (
                    <div className="text-[10px] text-slate-500 font-mono-data">
                      was selling as {o.matched_csv_sku}
                    </div>
                  )}
                </div>
                <XCircle size={14} className="text-[#4F46E5]" />
              </a>
            ))}
          </div>
        </div>

        {/* Dead SKUs */}
        <div data-testid="live-dead-skus">
          <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-3">
            Dead SKUs — listed but never sold
          </div>
          {(audit.dead_skus || []).length === 0 && (
            <div className="text-xs text-slate-500">Every live product has at least one order. ✓</div>
          )}
          <div className="space-y-2">
            {(audit.dead_skus || []).slice(0, 6).map((d, i) => (
              <a
                key={i}
                href={d.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 p-2.5 rounded-md bg-amber-50 hover:bg-amber-100 transition-colors"
              >
                {d.image_url ? (
                  <img src={d.image_url} alt="" className="w-10 h-10 object-cover rounded" />
                ) : (
                  <div className="w-10 h-10 bg-white/[0.05] rounded" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold truncate text-slate-900">{d.title}</div>
                  <div className="text-[10px] text-slate-600 font-mono-data">
                    ₹{Number(d.price || 0).toLocaleString("en-IN")}
                    {d.rating > 0 ? ` · ${d.rating}★` : ""}
                  </div>
                </div>
                <Archive size={14} className="text-amber-600" />
              </a>
            ))}
          </div>
        </div>

        {/* SEO tips */}
        <div data-testid="live-seo-tips">
          <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-3">
            Title SEO Tips — by Claude
          </div>
          {(audit.title_seo_tips || []).length === 0 && (
            <div className="text-xs text-slate-500">No titles captured for SEO audit.</div>
          )}
          <div className="space-y-2">
            {(audit.title_seo_tips || []).map((t, i) => (
              <div key={i} className="flex items-start gap-2 p-2.5 rounded-md bg-white/[0.02]">
                <Lightbulb size={14} className="text-amber-500 shrink-0 mt-0.5" />
                <div className="text-xs leading-relaxed">{t}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Rating risks */}
      {(audit.rating_risks || []).length > 0 && (
        <div className="mt-6" data-testid="live-rating-risks">
          <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-3">
            Rating Risks — below 3.5★ on live catalogue
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {audit.rating_risks.slice(0, 6).map((r, i) => (
              <a
                key={i}
                href={r.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 p-2.5 rounded-md bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
              >
                {r.image_url ? (
                  <img src={r.image_url} alt="" className="w-10 h-10 object-cover rounded" />
                ) : (
                  <div className="w-10 h-10 bg-white/[0.05] rounded" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold truncate">{r.title}</div>
                  <div className="text-[10px] text-slate-500">
                    {r.reviews.toLocaleString()} reviews
                  </div>
                </div>
                <span className="inline-flex items-center gap-0.5 text-xs font-bold text-[#4F46E5]">
                  <Star size={11} fill="currentColor" /> {r.rating}
                </span>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Insights() {
  const [reports, setReports] = useState([]);
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [crossPlatform, setCrossPlatform] = useState([]);

  useEffect(() => {
    api.get("/seller/reports").then((r) => {
      setReports(r.data);
      if (r.data[0]) setSelected(r.data[0].id);
    });
    api.get("/seller/analytics/cross-platform").then((r) => setCrossPlatform(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selected) return;
    api.get(`/seller/reports/${selected}`).then((r) => setData(r.data));
  }, [selected]);

  const summary = data?.summary || {};

  return (
    <AppShell role="seller">
      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#4F46E5] font-bold">Advanced Insights</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-2">Go deeper.</h1>
        </div>
        <select
          value={selected || ""}
          onChange={(e) => setSelected(e.target.value)}
          className="bg-white/[0.025] backdrop-blur-xl border border-white/[0.08] text-white px-4 py-2 rounded-md text-sm font-bold border border-white/[0.08] focus:outline-none focus:border-[#4F46E5]"
          data-testid="insights-report-selector"
        >
          {reports.length === 0 && <option>No reports</option>}
          {reports.map((r) => (
            <option key={r.id} value={r.id}>
              {r.platform} — {new Date(r.created_at).toLocaleString()}
            </option>
          ))}
        </select>
      </div>

      {!data && <div className="text-white/60">Pick a report above.</div>}

      {data && summary.live_audit && (
        <LiveAuditSection audit={summary.live_audit} />
      )}

      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Ads Plan */}
          <Section title="Ads — Scale" icon={Rocket} testId="insights-scale">
            <AdsByCategory
              items={summary.ads_recommendations?.scale || []}
              tone="scale"
              emptyText="No SCALE candidates found in this dataset."
            />
          </Section>

          <Section title="Ads — Avoid" icon={AlertTriangle} testId="insights-avoid">
            <AdsByCategory
              items={summary.ads_recommendations?.avoid || []}
              tone="avoid"
              emptyText="Nothing to cut — great signal."
            />
          </Section>

          {/* Budget */}
          <Section title="Ad Budget (15% of delivered sales)" icon={DollarSign} testId="insights-budget">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-white/[0.02]">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Monthly</div>
                <div className="font-mono-data text-2xl text-[#4F46E5]">{formatINR(summary.budget?.recommended_monthly_ad_budget)}</div>
              </div>
              <div className="p-4 rounded-lg bg-white/[0.02]">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Daily</div>
                <div className="font-mono-data text-2xl">{formatINR(summary.budget?.daily_ad_budget)}</div>
              </div>
            </div>
            <div className="text-[11px] text-slate-500 mt-2">Based on delivered sales of {formatINR(summary.budget?.delivered_sales)} — profit-agnostic.</div>
          </Section>

          {/* Demand Forecast removed permanently per product decision */}

          {/* Inventory */}
          <Section title="Inventory Alerts" icon={Package} testId="insights-inventory">
            <div className="grid grid-cols-1 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Low stock</div>
                <div className="space-y-1">
                  {(summary.inventory?.low_stock || []).slice(0, 5).map((s, i) => (
                    <div key={i} className="flex items-center justify-between text-xs font-bold">
                      <span>{s.sku}</span>
                      <span className="text-[#4F46E5] font-mono-data">{s.stock} left</span>
                    </div>
                  ))}
                  {(summary.inventory?.low_stock || []).length === 0 && (
                    <div className="text-xs text-slate-500">{summary.inventory?.note || "No low-stock items."}</div>
                  )}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 mt-3">Dead stock</div>
                <div className="space-y-1">
                  {(summary.inventory?.dead_stock || []).slice(0, 5).map((s, i) => (
                    <div key={i} className="flex items-center justify-between text-xs font-bold">
                      <span>{s.sku}</span>
                      <span className="text-slate-500 font-mono-data">{s.stock} units</span>
                    </div>
                  ))}
                  {(summary.inventory?.dead_stock || []).length === 0 && (
                    <div className="text-xs text-slate-500">No dead stock detected.</div>
                  )}
                </div>
              </div>

              {/* Closed SKUs — only rendered when the seller attached an
                  inventory file at upload time. Each row shows Style ID so
                  the seller can revive the listing directly on Myntra. */}
              {(summary.inventory?.closed_skus || []).length > 0 && (
                <div data-testid="insights-inventory-closed">
                  <div className="text-[10px] uppercase tracking-widest text-orange-700 mb-1 mt-3">
                    Closed listings — revive these
                    <span className="text-slate-400 ml-2 font-mono-data">
                      · {summary.inventory.closed_skus.length}
                    </span>
                  </div>
                  <div className="space-y-1">
                    {summary.inventory.closed_skus.slice(0, 8).map((s, i) => (
                      <div key={i} className="flex items-center justify-between text-xs font-bold gap-2">
                        <div className="min-w-0 flex-1 truncate">
                          <span className="text-slate-900">{s.style_name || s.sku}</span>
                          <span className="text-[10px] text-slate-500 font-mono-data ml-2">
                            SKU {s.sku}{s.style_id ? `  ·  Style ${s.style_id}` : ""}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Finished / out-of-stock SKUs — restock signals. */}
              {(summary.inventory?.finished_skus || []).length > 0 && (
                <div data-testid="insights-inventory-finished">
                  <div className="text-[10px] uppercase tracking-widest text-rose-700 mb-1 mt-3">
                    Finished / out-of-stock — restock now
                    <span className="text-slate-400 ml-2 font-mono-data">
                      · {summary.inventory.finished_skus.length}
                    </span>
                  </div>
                  <div className="space-y-1">
                    {summary.inventory.finished_skus.slice(0, 8).map((s, i) => (
                      <div key={i} className="flex items-center justify-between text-xs font-bold gap-2">
                        <div className="min-w-0 flex-1 truncate">
                          <span className="text-slate-900">{s.style_name || s.sku}</span>
                          <span className="text-[10px] text-slate-500 font-mono-data ml-2">
                            SKU {s.sku}{s.style_id ? `  ·  Style ${s.style_id}` : ""}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {summary.inventory?.inventory_note && (
                <div className="text-[11px] text-slate-500 italic mt-2">
                  {summary.inventory.inventory_note}
                </div>
              )}
            </div>
          </Section>

          {/* Reviews */}
          {/* Review Sentiment removed permanently per product decision */}

          {/* Pricing */}
          <Section title="Pricing Engine" icon={TrendingUp} testId="insights-pricing">
            <div className="space-y-2">
              {(summary.pricing_suggestions || []).slice(0, 6).map((p, i) => {
                const up = p.action === "increase";
                const down = p.action === "decrease";
                const deltaPct = p.delta_pct ?? 0;
                const deltaAbs = Math.abs(deltaPct);
                return (
                  <div
                    key={i}
                    className="flex items-center justify-between gap-3 p-3 rounded-md bg-white/[0.02]"
                    data-testid={`pricing-row-${i}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-bold truncate">{p.sku}</div>
                      <div className="text-[10px] text-slate-500">{p.reason}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs font-mono-data text-slate-500 line-through">
                        {formatINR(p.current_price)}
                      </span>
                      {up ? (
                        <TrendingUp size={14} className="text-green-600" />
                      ) : down ? (
                        <TrendingDown size={14} className="text-[#4F46E5]" />
                      ) : (
                        <span className="text-xs text-slate-500">→</span>
                      )}
                      <span
                        className={`text-sm font-mono-data font-bold ${
                          up ? "text-green-700" : down ? "text-[#4F46E5]" : "text-slate-300"
                        }`}
                      >
                        {formatINR(p.suggested_price)}
                      </span>
                      {deltaAbs > 0 && (
                        <span
                          className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${
                            up
                              ? "bg-green-100 text-green-700"
                              : down
                              ? "bg-red-100 text-[#4F46E5]"
                              : "bg-white/[0.05] text-slate-300"
                          }`}
                          data-testid={`pricing-delta-${i}`}
                        >
                          {up ? "↑" : down ? "↓" : ""} {deltaAbs}%
                        </span>
                      )}
                      {deltaAbs === 0 && (
                        <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-white/[0.05] text-slate-300">
                          HOLD
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              {(summary.pricing_suggestions || []).length === 0 && (
                <div className="text-xs text-slate-500">Pricing data unavailable for this upload.</div>
              )}
            </div>
          </Section>

          {/* Quick commerce */}
          {summary.quick_commerce?.enabled && (
            <Section title="Quick Commerce Insights" icon={Zap} testId="insights-quick-commerce">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="p-3 bg-white/[0.02] rounded-md">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Avg delivery</div>
                  <div className="font-mono-data text-xl">{summary.quick_commerce.delivery_performance?.avg_minutes ?? "—"}m</div>
                </div>
                <div className="p-3 bg-white/[0.02] rounded-md">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Turnover</div>
                  <div className="font-mono-data text-xl">{summary.quick_commerce.inventory_turnover ?? "—"}</div>
                </div>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Peak hours</div>
              <div className="flex flex-wrap gap-1">
                {(summary.quick_commerce.peak_hours || []).map((h, i) => (
                  <span key={i} className="text-[11px] font-mono-data bg-[#4F46E5] text-white px-2 py-1 rounded">
                    {h.hour}h · {h.orders}
                  </span>
                ))}
              </div>
            </Section>
          )}

          {/* Competitor Landscape removed permanently per product decision */}

          {/* Article types / Product categories */}
          {(summary.article_types || []).length > 0 && (
            <Section title="Product Categories" icon={Tag} testId="insights-article-types">
              <div className="space-y-2">
                {summary.article_types.slice(0, 8).map((a, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 rounded-md bg-white/[0.02]">
                    <div>
                      <div className="text-xs font-bold">{a.category}</div>
                      <div className="text-[10px] text-slate-500 font-mono-data">{a.orders} orders</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs font-mono-data">{formatINR(a.sales)}</div>
                      <div className="text-[10px] text-green-700 font-mono-data">{formatINR(a.delivered_sales)} delivered</div>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Cancellation reasons */}
          {(summary.cancellation_reasons || []).length > 0 && (
            <Section title="Why Orders Cancel" icon={XCircle} testId="insights-cancel-reasons">
              <div className="space-y-2">
                {summary.cancellation_reasons.slice(0, 8).map((c, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 rounded-md bg-red-50">
                    <span className="text-xs font-bold text-slate-900">{c.reason}</span>
                    <span className="text-xs font-mono-data text-[#4F46E5]">{c.count}</span>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* RTO Orders — total count + per-SKU drill-down with Style ID */}
          {(summary.rto_orders?.skus || []).length > 0 && (
            <Section
              title={`RTO Orders — ${summary.rto_orders.total || 0} orders · ${summary.rto_orders.rate_pct || 0}%`}
              icon={Package}
              testId="insights-rto-orders"
            >
              <div className="space-y-2">
                {summary.rto_orders.skus.slice(0, 10).map((r, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between gap-3 p-2.5 rounded-md bg-orange-50"
                    data-testid={`rto-row-${i}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-bold truncate text-slate-900">
                        {r.style_name || r.sku}
                      </div>
                      <div className="text-[10px] text-slate-500 font-mono-data truncate">
                        SKU · {r.sku}
                        {r.style_id ? `  ·  Style ID · ${r.style_id}` : ""}
                        {r.category ? `  ·  ${r.category}` : ""}
                      </div>
                    </div>
                    <span className="text-xs font-mono-data text-orange-700 shrink-0">
                      {r.count} RTO
                    </span>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>
      )}

      {/* Cross-platform SKU comparator */}
      {crossPlatform.length > 0 && (
        <div className="mt-8 bg-white/[0.025] backdrop-blur-xl border border-white/[0.08] rounded-xl p-6 text-white" data-testid="cross-platform-comparator">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign size={16} className="text-[#4F46E5]" />
            <div className="text-sm font-bold uppercase tracking-[0.2em]">Cross-Platform SKU Comparator</div>
          </div>
          <div className="text-xs text-slate-500 mb-4">SKUs that appear in multiple platform reports — spot arbitrage, inventory imbalance, and winner markets at a glance.</div>
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-white/[0.08]">
                  <th className="py-2">SKU</th>
                  <th className="py-2">Platforms</th>
                  <th className="py-2 text-right">Sales split</th>
                  <th className="py-2 text-right">Total Sales</th>
                  <th className="py-2 text-right">Total Orders</th>
                </tr>
              </thead>
              <tbody>
                {crossPlatform.map((row, i) => (
                  <tr key={i} className="border-b border-zinc-100">
                    <td className="py-2 font-bold text-xs">{row.sku}</td>
                    <td className="py-2">
                      <div className="flex flex-wrap gap-1">
                        {Object.keys(row.platforms).map((p) => (
                          <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-[#4F46E5] text-white font-bold">{p}</span>
                        ))}
                      </div>
                    </td>
                    <td className="py-2 text-right text-[11px] font-mono-data text-slate-400">
                      {Object.entries(row.platforms).map(([p, v]) => (
                        <div key={p}>{p}: {formatINR(v.revenue)}</div>
                      ))}
                    </td>
                    <td className="py-2 text-right font-mono-data">{formatINR(row.total_revenue)}</td>
                    <td className="py-2 text-right font-mono-data text-[#4F46E5]">{row.total_orders ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AppShell>
  );
}
